import { useState, type FormEvent, type ReactNode } from 'react';
import { useApi, apiPost, Cargando, ErrorApi, BotonAccion, diaNombre, diaNombre as dn } from '../shared/ui.tsx';
import type { Disponibilidad } from '../shared/tipos.ts';

export function MiDisponibilidadPage(): ReactNode {
  const { datos, cargando, error, recargar } = useApi<Disponibilidad[]>('/api/personal/disponibilidad');

  if (cargando) return <Cargando />;
  if (error) return <ErrorApi mensaje={error} onReintentar={recargar} />;

  const semanales = (datos ?? []).filter((d) => d.tipo === 'semanal');
  const bloqueos = (datos ?? []).filter((d) => d.tipo === 'bloqueo');

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-bold text-neutro-800">Mi disponibilidad</h2>
      <p className="text-sm text-neutro-400">
        El sistema no te convoca fuera de tu disponibilidad declarada (salvo excepción manual del admin).
      </p>

      <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Disponibilidad habitual</h3>
        {semanales.length > 0 ? (
          <ul className="mt-2 space-y-1 text-sm">
            {semanales.map((d) => (
              <li key={d.id} className="flex items-center justify-between border-b border-contorno-var py-1.5">
                <span>{dn(d.dia_semana ?? 0)} · {d.desde}–{d.hasta}</span>
                <button className="text-xs text-estado-error hover:underline" onClick={async () => {
                  await apiPost(`/api/personal/disponibilidad/${d.id}`, undefined, 'DELETE');
                  recargar();
                }}>Quitar</button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-neutro-400">Sin disponibilidad semanal declarada.</p>
        )}
        <AgregarSemanal existentes={semanales} onAgregada={recargar} />
      </section>

      <section className="rounded-2xl bg-white p-4 shadow-sm border border-contorno-var">
        <h3 className="font-semibold text-neutro-800">Bloqueos puntuales</h3>
        {bloqueos.length > 0 ? (
          <ul className="mt-2 space-y-1 text-sm">
            {bloqueos.map((d) => (
              <li key={d.id} className="flex items-center justify-between border-b border-contorno-var py-1.5">
                <span>{d.fecha} · {d.desde}–{d.hasta}</span>
                <button className="text-xs text-estado-error hover:underline" onClick={async () => {
                  await apiPost(`/api/personal/disponibilidad/${d.id}`, undefined, 'DELETE');
                  recargar();
                }}>Quitar</button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-2 text-xs text-neutro-400">Sin bloqueos.</p>
        )}
        <AgregarBloqueo onAgregada={recargar} />
      </section>
    </div>
  );
}

function AgregarSemanal({ existentes, onAgregada }: { existentes: Disponibilidad[]; onAgregada: () => void }): ReactNode {
  const [dias, setDias] = useState<number[]>([]);
  const [desde, setDesde] = useState('00:00');
  const [hasta, setHasta] = useState('23:59');
  const [agregando, setAgregando] = useState(false);

  function toggle(dia: number) {
    setDias((prev) => prev.includes(dia) ? prev.filter((d) => d !== dia) : [...prev, dia]);
  }

  async function agregar(e: FormEvent) {
    e.preventDefault();
    if (dias.length === 0) return;
    setAgregando(true);
    try {
      for (const dia of dias) {
        // No duplicar si ya existe exactamente el mismo rango para ese día.
        if (existentes.some((d) => d.dia_semana === dia && d.desde === desde && d.hasta === hasta)) continue;
        await apiPost('/api/personal/disponibilidad', { tipo: 'semanal', dia_semana: dia, desde, hasta });
      }
      setDias([]);
      onAgregada();
    } catch (e2) { alert(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setAgregando(false); }
  }

  return (
    <form onSubmit={agregar} className="mt-3 space-y-2 rounded-lg bg-superficie-100/50 p-3 text-sm">
      <p className="text-xs text-neutro-400">Tocá los días en los que estás disponible (horario preseteado a día completo, editable):</p>
      <div className="flex flex-wrap gap-1.5">
        {[1, 2, 3, 4, 5, 6, 7].map((d) => {
          const activo = dias.includes(d);
          const cubierto = existentes.some((e2) => e2.dia_semana === d);
          return (
            <button key={d} type="button" onClick={() => toggle(d)}
              className={`rounded-full px-3.5 py-1.5 font-medium transition-colors ${
                activo ? 'bg-primario-400 text-white'
                : cubierto ? 'border border-dashed border-primario-400 bg-white text-neutro-800'
                : 'border border-contorno-var bg-white text-neutro-800'
              }`}>
              {diaNombre(d)}
            </button>
          );
        })}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <label className="flex items-center gap-1.5">
          <span className="text-xs text-neutro-400">Desde</span>
          <input className="campo w-24" type="time" value={desde} onChange={(e) => setDesde(e.target.value)} />
        </label>
        <label className="flex items-center gap-1.5">
          <span className="text-xs text-neutro-400">Hasta</span>
          <input className="campo w-24" type="time" value={hasta} onChange={(e) => setHasta(e.target.value)} />
        </label>
        <BotonAccion type="submit" variante="secundario" disabled={agregando || dias.length === 0}>
          {agregando ? 'Agregando…' : dias.length > 0 ? `+ Agregar ${dias.length} día${dias.length > 1 ? 's' : ''}` : '+ Agregar'}
        </BotonAccion>
      </div>
    </form>
  );
}

function AgregarBloqueo({ onAgregada }: { onAgregada: () => void }): ReactNode {
  const [fecha, setFecha] = useState('');
  const [desde, setDesde] = useState('00:00');
  const [hasta, setHasta] = useState('23:59');
  const [agregando, setAgregando] = useState(false);

  async function agregar(e: FormEvent) {
    e.preventDefault();
    if (!fecha) { alert('Elegí una fecha'); return; }
    setAgregando(true);
    try {
      await apiPost('/api/personal/disponibilidad', { tipo: 'bloqueo', fecha, desde, hasta });
      onAgregada();
    } catch (e2) { alert(e2 instanceof Error ? e2.message : 'Error'); }
    finally { setAgregando(false); }
  }

  return (
    <form onSubmit={agregar} className="mt-3 flex flex-wrap gap-2 rounded-lg bg-superficie-100/50 p-2 text-sm">
      <input className="campo w-36" type="date" value={fecha} onChange={(e) => setFecha(e.target.value)} />
      <input className="campo w-24" type="time" value={desde} onChange={(e) => setDesde(e.target.value)} />
      <input className="campo w-24" type="time" value={hasta} onChange={(e) => setHasta(e.target.value)} />
      <BotonAccion type="submit" variante="secundario" disabled={agregando}>+ Bloquear</BotonAccion>
    </form>
  );
}